import { BoolNode } from 'src/services/common.types';
import { JobsResponse } from 'src/services/jobs/jobs.types';
import { Contact } from 'src/services/contacts/contacts.types';
import { Activity } from 'src/services/activities/activities.types';

import { rootApi } from '../rootApi';
import { DocumentType } from '../files/file.types';
import { ReferenceRequestResponse } from '../references/references.types';
import { SortConfig, APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  IOrgCandidate,
  CandidateProfile,
  CancelInviteProps,
  WriteBackFieldKey,
  CandidateProfileUpdate,
  ICandidateReferee,
  CandidateImportJob,
  ICandidateDocument,
  InviteCandidatesDTO,
  AddProfileRefereeDto,
  TransformDslResponse,
  GetCandidateJobsProps,
  GetOrgCandidatesQuery,
  IncomingCompanyInvite,
  IOrgCandidateListItem,
  OutgoingCompanyInvite,
  PublicCandidateInvite,
  CreateCompanyInviteDto,
  ExportSettingsResponse,
  GetCompanyInvitesProps,
  IncomingCandidateInvite,
  OutgoingCandidateInvite,
  UpdateAvailabilityProps,
  UpdateProfileRefereeDto,
  VerificationActionProps,
  ActionCompanyInviteProps,
  GetCandidateInvitesProps,
  InviteCandidatesResponse,
  RequestVerificationProps,
  ICandidateProfileDocument,
  UploadProfileDocumentData,
  ActionCandidateInviteProps,
  CreateCandidateDocumentDto,
  CreateUnlinkedCandidateDto,
  ImportUnlinkedCandidateDto,
  UpdateUnlinkedCandidateDto,
  AvailabilityProfileResponse,
  SetCandidateProfileResponse,
  ReferenceSubmissionResponse,
  CandidateApplicationsResponse,
  ExperienceVerificationRequest
} from './candidates.types';

export const candidatesApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyProfile: builder.query<CandidateProfile, void>({
      query: () => ({
        url: '/candidates/my/profile',
        method: HTTPRequestType.GET,
      }),
      providesTags: ['Profile'],
    }),

    getCandidateProfile: builder.query<CandidateProfile, string>({
      query: (id: string) => ({
        url: `/candidates/profiles/${id}`,
        method: HTTPRequestType.GET,
      }),
      providesTags: (result, error, args) => [{ type: 'CandidateProfile', id: args }],
    }),

    getCandidateProfileByOrganization: builder.query<CandidateProfile, string>({
      query: (id: string) => ({
        url: `/candidates/organizations/${id}/profile`,
        method: HTTPRequestType.GET,
      })
    }),

    setCandidateProfile: builder.mutation<
      SetCandidateProfileResponse,
      { id: string; data: CandidateProfileUpdate }
    >({
      query: ({ id, data }) => ({
        url: `/candidates/profiles/${id}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CandidateProfile', id }, 'Profile', 'OrgCandidates'],
    }),

    requestExperienceVerification: builder.mutation<APIResponse, RequestVerificationProps>({
      query: ({ work_experience_id, data }: RequestVerificationProps) => ({
        url: `/candidates/work-history/${work_experience_id}/verification/request`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Profile'],
    }),

    getExperienceVerificationRequest: builder.query<ExperienceVerificationRequest, string>({
      query: (work_experience_id: string) => ({
        url: `/candidates/work-history/${work_experience_id}`,
        method: HTTPRequestType.GET,
      }),
    }),

    actionExperienceVerification: builder.mutation<APIResponse, VerificationActionProps>({
      query: ({ work_experience_id, data }: VerificationActionProps) => ({
        url: `/candidates/work-history/${work_experience_id}/verification/action`,
        method: HTTPRequestType.POST,
        data,
      }),
    }),

    // Document endpoints -----------------------------------------------------
    importResume: builder.mutation<APIResponse, { document_id: string, add_to_profile?: boolean }>({
      query: ({ document_id, add_to_profile = true }: { document_id: string, add_to_profile?: boolean }) => ({
        url: '/candidates/features/import-resume',
        method: HTTPRequestType.POST,
        data: {
          document_id,
          add_to_profile
        },
      }),
      invalidatesTags: ['Profile'],
    }),

    getProfileDocuments: builder.query<PaginatedResponse<ICandidateProfileDocument>, { profileId: string; params: { search_query?: string; type?: DocumentType } & PageOptions }>({
      query: ({ params, profileId }) => ({
        url: `/candidates/profiles/${profileId}/documents`,
        params,
      }),
      providesTags: ['Documents'],
    }),

    attachDocumentToProfile: builder.mutation<APIResponse, { data: UploadProfileDocumentData; profileId: string }>({
      query: ({ data, profileId }) => ({
        url: `/candidates/profiles/${profileId}/documents`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Documents'],
    }),

    updateProfileDocument: builder.mutation<APIResponse, { documentId: string; data: Partial<Pick<UploadProfileDocumentData, 'expiry_date' | 'visibility_rule'>> & { shared_with_guest?: boolean; name?: string | null }; }>({
      query: ({ documentId, data }) => ({
        url: `/candidates/profiles/documents/${documentId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: ['Documents'],
    }),

    // A mutation rather than a query: the link is short-lived, so every download asks for a fresh one.
    getProfileDocumentDownloadUrl: builder.mutation<{ url: string }, string>({
      query: (documentId: string) => ({
        url: `/candidates/profiles/documents/${documentId}/download`,
        method: HTTPRequestType.GET,
      }),
    }),

    deleteProfileDocument: builder.mutation<APIResponse, string>({
      query: (documentId: string) => ({
        url: `/candidates/profiles/documents/${documentId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: ['Documents'],
    }),

    // Availability endpoints -------------------------------------------------
    getProfileAvailability: builder.query<AvailabilityProfileResponse, void>({
      query: () => ({
        url: `/candidates/my/availability`,
        method: HTTPRequestType.GET,
      }),
      providesTags: ['ProfileAvailability'],
    }),

    updateAvailability: builder.mutation<APIResponse, UpdateAvailabilityProps>({
      query: (data) => ({
        url: '/candidates/my/availability',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['ProfileAvailability', 'Profile'],
    }),

    updateAvailabilityAccess: builder.mutation<APIResponse, { subscriber_organization: string; status: boolean }>({
      query: ({ subscriber_organization, status }) => ({
        url: `/candidates/my/availability/access`,
        method: HTTPRequestType.POST,
        data: {
          subscriber_organization,
          status,
        },
      }),
      invalidatesTags: ['Recruiters'],
    }),

    // org candidate invites
    getIncomingOrgCandidateInvites: builder.query<PaginatedResponse<IncomingCandidateInvite>, GetCandidateInvitesProps>({
      query: (data: GetCandidateInvitesProps) => ({
        url: `/my/candidates/candidate-invites/incoming`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: (data) => ['IncomingCandidateInvites'],
    }),

    getOutgoingOrgCandidateInvites: builder.query<PaginatedResponse<OutgoingCandidateInvite>, GetCandidateInvitesProps>({
      query: (data: GetCandidateInvitesProps) => ({
        url: `/my/candidates/candidate-invites/outgoing`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: ['OutgoingCandidateInvites'],
    }),

    // org candidate company invites
    getIncomingCompanyInvites: builder.query<PaginatedResponse<IncomingCompanyInvite>, GetCompanyInvitesProps>({
      query: (data: GetCandidateInvitesProps) => ({
        url: `/my/candidates/company-invites/incoming`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: (data) => ['IncomingCompanyInvites'],
    }),

    getOutgoingCompanyInvites: builder.query<PaginatedResponse<OutgoingCompanyInvite>, GetCompanyInvitesProps>({
      query: (data: GetCandidateInvitesProps) => ({
        url: `/my/candidates/company-invites/outgoing`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: (data) => ['OutgoingCompanyInvites'],
    }),

    createCompanyInvite: builder.mutation<APIResponse, CreateCompanyInviteDto>({
      query: (data: CreateCompanyInviteDto) => ({
        url: `/my/candidates/company-invites`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['OutgoingCompanyInvites'],
    }),

    actionCompanyInvite: builder.mutation<APIResponse, ActionCompanyInviteProps>({
      query: (data: ActionCompanyInviteProps) => ({
        url: `/my/candidates/company-invites/${data.inviteId}/action`,
        method: HTTPRequestType.POST,
        data: {
          action: data.action,
        },
      }),
      invalidatesTags: (_res, _err, args) => {
        if (args.action === 'accept') {
          return ['IncomingCompanyInvites', 'OrgCandidates'];
        }
        return ['IncomingCompanyInvites'];
      },
    }),

    cancelCompanyInvite: builder.mutation<APIResponse, string>({
      query: (inviteId) => ({
        url: `/my/candidates/company-invites/${inviteId}/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['OutgoingCompanyInvites'],
    }),

    inviteOrgCandidates: builder.mutation<InviteCandidatesResponse[], InviteCandidatesDTO>({
      query: (data) => ({
        url: `/my/candidates/candidate-invites`,
        method: HTTPRequestType.POST,
        data: { invites: data.invites },
      }),
      invalidatesTags: ['OutgoingCandidateInvites'],
    }),

    actionOrgCandidateInvite: builder.mutation<APIResponse, ActionCandidateInviteProps>({
      query: (data) => ({
        url: `/my/candidates/candidate-invites/${data.inviteId}/action`,
        method: HTTPRequestType.POST,
        data: {
          action: data.action,
        },
      }),
      invalidatesTags: (_res, _err, args) => {
        if (args.action === 'accept') {
          return ['IncomingCandidateInvites', 'Recruiters', 'Clients'];
        }
        return ['IncomingCandidateInvites'];
      },
    }),

    cancelOrgCandidateInvite: builder.mutation<APIResponse, CancelInviteProps>({
      query: (data) => ({
        url: `/my/candidates/candidate-invites/${data.inviteId}/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['OutgoingCandidateInvites'],
    }),

    // jobs
    getCandidateJobs: builder.query<JobsResponse, GetCandidateJobsProps>({
      query: (data: GetCandidateJobsProps) => ({
        url: `/my/candidates/${data?.candidateId}/jobs`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: ['CandidateJobs'],
    }),
    getCandidateApplications: builder.query<CandidateApplicationsResponse, GetCandidateJobsProps>({
      query: (data: GetCandidateJobsProps) => ({
        url: `/my/candidates/${data?.candidateId}/applications`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: ['CandidateApplications'],
    }),

    getExportSettings: builder.query<ExportSettingsResponse, string>({
      query: (profileId: string) => ({
        url: `/candidates/profiles/${profileId}/export/settings`,
        method: HTTPRequestType.GET,
      }),
    }),

    getProfileReferees: builder.query<PaginatedResponse<ICandidateReferee>, { profileId: string, pageOptions: PageOptions }>({
      query: ({ profileId, pageOptions }) => ({
        url: `/candidates/profiles/${profileId}/referees`,
        method: HTTPRequestType.GET,
        params: {
          page: pageOptions.page,
          per_page: pageOptions.per_page
        },
      }),
      providesTags: (result, error, { profileId }) => [{ type: 'ProfileReferees', id: profileId }],
    }),
    getProfileRefereeById: builder.query<ICandidateReferee, { profileId: string, refereeId: string }>({
      query: ({ profileId, refereeId }) => ({
        url: `/candidates/profiles/${profileId}/referees/${refereeId}`,
        method: HTTPRequestType.GET,
      }),
    }),

    createProfileReferee: builder.mutation<APIResponse, { profileId: string, data: AddProfileRefereeDto }>({
      query: ({ profileId, data }) => ({
        url: `/candidates/profiles/${profileId}/referees`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { profileId }) => [{ type: 'ProfileReferees', id: profileId }]
    }),

    updateProfileReferee: builder.mutation<APIResponse, { profileId: string, refereeId: string, data: UpdateProfileRefereeDto }>({
      query: ({ profileId, refereeId, data }) => ({
        url: `/candidates/profiles/${profileId}/referees/${refereeId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (result, error, { profileId }) => [{ type: 'ProfileReferees', id: profileId }]
    }),

    deleteProfileReferee: builder.mutation<APIResponse, { profileId: string, refereeId: string }>({
      query: ({ profileId, refereeId }) => ({
        url: `/candidates/profiles/${profileId}/referees/${refereeId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (result, error, { profileId }) => [{ type: 'ProfileReferees', id: profileId }]
    }),
    getRefereeRequests: builder.query<PaginatedResponse<ReferenceRequestResponse>, { profileId: string, refereeId: string, pageOptions: PageOptions }>({
      query: ({ profileId, refereeId, pageOptions }) => ({
        url: `/candidates/profiles/${profileId}/referees/${refereeId}/requests`,
        method: HTTPRequestType.GET,
        params: {
          page: pageOptions.page,
          per_page: pageOptions.per_page
        },
      }),
    }),
    getRequestSubmissions: builder.query<PaginatedResponse<ReferenceSubmissionResponse>, { profileId: string, refereeId: string, requestId: string, pageOptions: PageOptions }>({
      query: ({ profileId, refereeId, requestId, pageOptions }) => ({
        url: `/candidates/profiles/${profileId}/referees/${refereeId}/requests/${requestId}/submissions`,
        method: HTTPRequestType.GET,
        params: {
          page: pageOptions.page,
          per_page: pageOptions.per_page
        },
      }),
    }),

    getOrganizationCandidates: builder.query<PaginatedResponse<IOrgCandidate>, GetOrgCandidatesQuery & { page: PageOptions, sortConfig?: SortConfig }>({
      query: (data) => ({
        url: `/my/candidates`,
        params: {
          search_query: data.search_query,
          availability_status: data.availability_status,
          skills: data.skills,
          labels: data.labels,
          ...data.page,
          ...(data.sortConfig ?? {})
        },
      }),
      providesTags: () => ['OrgCandidates'],
    }),
    // Lean list read path for the shared list component (JT1-1527). Separate from
    // getOrganizationCandidates (which six other screens depend on) and returns the flat
    // IOrgCandidateListItem. Accepts an arbitrary sort field from the expanded whitelist.
    getMyCandidatesLean: builder.query<PaginatedResponse<IOrgCandidateListItem>, GetOrgCandidatesQuery & { page: PageOptions; sortConfig?: SortConfig }>({
      query: (data) => ({
        url: `/my/candidates/list`,
        params: {
          search_query: data.search_query,
          availability_status: data.availability_status,
          skills: data.skills,
          labels: data.labels,
          list_id: data.list_id,
          // Per-column search values, serialized as JSON (item 3). Omitted when empty.
          ...(data.column_filters && Object.keys(data.column_filters).length
            ? { column_filters: JSON.stringify(data.column_filters) }
            : {}),
          ...data.page,
          ...(data.sortConfig ?? {}),
        },
      }),
      providesTags: () => ['OrgCandidates'],
    }),
    // Advanced search on the LEAN read path (JT1-1532): POST the DSL, get back the same flat
    // IOrgCandidateListItem the shared DataList renders, so advanced results behave like every list.
    searchMyCandidatesLean: builder.query<PaginatedResponse<IOrgCandidateListItem>, { search_dsl: BoolNode; page: PageOptions; sortConfig?: SortConfig; column_filters?: Record<string, string> }>({
      query: (data) => ({
        url: `/my/candidates/list`,
        method: HTTPRequestType.POST,
        data: data.search_dsl,
        params: {
          ...(data.column_filters && Object.keys(data.column_filters).length
            ? { column_filters: JSON.stringify(data.column_filters) }
            : {}),
          ...data.page,
          ...(data.sortConfig ?? {}),
        },
      }),
      providesTags: () => ['OrgCandidates'],
    }),
    transformSearchDSL: builder.mutation<TransformDslResponse, { text: string }>({
      query: ({ text }) => ({
        url: `/my/candidates/search/transform-dsl`,
        method: HTTPRequestType.POST,
        data: { text },
      }),
    }),

    getOrganizationCandidate: builder.query<IOrgCandidate, string>({
      query: (id: string) => ({
        url: `/my/candidates/${id}`,
        method: HTTPRequestType.GET,
      }),
      providesTags: (result, error, args) => [{ type: 'OrgCandidates', id: args }],
    }),

    // The full live (synced) profile, fetched only when the Live Profile tab is opened (JT1-1605).
    // GET /my/candidates/:id used to build and return this inline on every candidate open even though
    // the tab is not the default, which made the detail endpoint build two complete profile graphs.
    // The candidate payload still carries the live profile's id, location and title.
    getOrganizationCandidateSyncedProfile: builder.query<CandidateProfile | null, string>({
      query: (id: string) => ({
        url: `/my/candidates/${id}/synced-profile`,
        method: HTTPRequestType.GET,
      }),
      providesTags: (result, error, args) => [{ type: 'OrgCandidates', id: `${args}-synced-profile` }],
    }),

    unlinkCandidate: builder.mutation<APIResponse, string>({
      query: (candidateId: string) => ({
        url: `/my/candidates/${candidateId}/unlink`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['OrgCandidates'],
    }),
    removeCandidate: builder.mutation<APIResponse, string>({
      query: (candidateId: string) => ({
        url: `/my/candidates/${candidateId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: ['OrgCandidates'],
    }),
    updateCandidateRating: builder.mutation<APIResponse, { candidateId: string; rating: number }>({
      query: ({ candidateId, rating }) => ({
        url: `/my/candidates/${candidateId}/rating`,
        method: HTTPRequestType.POST,
        data: { rating },
      }),
      invalidatesTags: (result, error, { candidateId }) => ['OrgCandidates', { type: 'OrgCandidates', id: candidateId }],
    }),

    createUnlinkedCandidate: builder.mutation<APIResponse, { data: CreateUnlinkedCandidateDto }>({
      query: ({ data }) => ({
        url: `/my/candidates`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['OrgCandidates'],
    }),
    importUnlinkedCandidate: builder.mutation<APIResponse, { data: ImportUnlinkedCandidateDto }>({
      query: ({ data }) => ({
        url: `/my/candidates/import`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['OrgCandidates'],
    }),
    csvImportCandidates: builder.mutation<Array<{ email: string; status: 'success' | 'failed'; message?: string }>, {
      organization_id?: string,
      candidates: {
        first_name: string;
        last_name: string;
        email: string;
        position_title?: string;
        country_code?: string;
        phone_number?: string;
        special_information?: string;
        linkedin_link?: string;
        x_link?: string;
        facebook_link?: string;
        github_link?: string;
      }[]
    }>({
      query: ({ organization_id, candidates }) => ({
        url: `/my/candidates/csv-import`,
        method: HTTPRequestType.POST,
        data: {
          organization_id,
          data: candidates
        },
      }),
      invalidatesTags: ['OrgCandidates'],
    }),
    createCandidateImportJob: builder.mutation<CandidateImportJob, { organization_id: string; file_ids: string[], create_invite: boolean }>({
      query: ({ organization_id, file_ids, create_invite }) => ({
        url: `/my/candidates/file-import`,
        method: HTTPRequestType.POST,
        data: {
          organization_id,
          file_ids,
          create_invite
        },
      })
    }),
    getCandidateImportJob: builder.query<CandidateImportJob, string>({
      query: (jobId: string) => ({
        url: `/my/candidates/file-import/${jobId}`,
        method: HTTPRequestType.GET,
      })
    }),

    updateUnlinkedCandidate: builder.mutation<APIResponse, { candidateId: string; data: UpdateUnlinkedCandidateDto }>({
      query: ({ candidateId, data }) => ({
        url: `/my/candidates/${candidateId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (result, error, { candidateId }) => ['OrgCandidates', { type: 'OrgCandidates', id: candidateId }],
    }),

    createDirectInvite: builder.mutation<APIResponse, { candidateId: string }>({
      query: ({ candidateId }) => ({
        url: `/my/candidates/${candidateId}/invites`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['OrgCandidates'],
    }),

    getPublicCandidateInviteById: builder.query<PublicCandidateInvite, string>({
      query: (id) => ({
        url: `/candidates/candidate-invites/${id}`,
      }),
    }),

    importCandidateProfile: builder.mutation<APIResponse, { candidateId: string; documentId: string; add_to_profile?: boolean; }>({
      query: ({ candidateId, documentId, add_to_profile = false }) => ({
        url: `/my/candidates/${candidateId}/import-resume`,
        method: HTTPRequestType.POST,
        data: {
          document_id: documentId,
          add_to_profile
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'OrgCandidates', id: arg.candidateId }]
    }),

    // documents
    getCandidateDocuments: builder.query<PaginatedResponse<ICandidateDocument>, { candidate_id: string, params: PageOptions & { search_query?: string, type?: DocumentType } }>({
      query: ({ candidate_id, params }) => ({
        url: `/my/candidates/${candidate_id}/documents`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CandidateDocuments', id: args.candidate_id }],
    }),
    createCandidateDocument: builder.mutation<APIResponse, { candidate_id: string, data: CreateCandidateDocumentDto }>({
      query: ({ candidate_id, data }) => ({
        url: `/my/candidates/${candidate_id}/documents`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CandidateDocuments', id: args.candidate_id }],
    }),
    removeCandidateDocument: builder.mutation<APIResponse, { candidate_id: string, candidate_doc_id: string }>({
      query: ({ candidate_id, candidate_doc_id }) => ({
        url: `/my/candidates/${candidate_id}/documents/${candidate_doc_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CandidateDocuments', id: args.candidate_id }],
    }),
    updateCandidateDocument: builder.mutation<APIResponse, { candidate_id: string, candidate_doc_id: string, data: { name?: string | null } }>({
      query: ({ candidate_id, candidate_doc_id, data }) => ({
        url: `/my/candidates/${candidate_id}/documents/${candidate_doc_id}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CandidateDocuments', id: args.candidate_id }],
    }),
    // A mutation rather than a query: the link is short-lived, so every download asks for a fresh one.
    getCandidateDocumentDownloadUrl: builder.mutation<{ url: string }, { candidate_id: string, candidate_doc_id: string }>({
      query: ({ candidate_id, candidate_doc_id }) => ({
        url: `/my/candidates/${candidate_id}/documents/${candidate_doc_id}/download`,
        method: HTTPRequestType.GET,
      }),
    }),

    // contacts
    getLinkedContacts: builder.query<PaginatedResponse<Contact>, { candidate_id: string, page: number, per_page: number }>({
      query: ({ candidate_id, page, per_page }) => ({
        url: `/my/candidates/${candidate_id}/contacts`,
        params: {
          page,
          per_page
        }
      }),
      providesTags: (_res, _err, args) => [{ type: 'LinkedCandidateContacts', id: args.candidate_id }],
    }),

    getOrgCandidateActivities: builder.query<PaginatedResponse<Activity>, { candidate_id: string; page: number; perPage: number }>({
      query: ({ candidate_id, page, perPage }) => ({
        url: `/my/candidates/${candidate_id}/activities`,
        params: { page, per_page: perPage },
      }),
    }),

  }),
});

export const {
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  useGetCandidateProfileByOrganizationQuery,
  useGetCandidateProfileQuery,
  useSetCandidateProfileMutation,
  useRequestExperienceVerificationMutation,
  useGetExperienceVerificationRequestQuery,
  useActionExperienceVerificationMutation,
  useImportResumeMutation,

  useGetProfileDocumentsQuery,
  useAttachDocumentToProfileMutation,
  useUpdateProfileDocumentMutation,
  useGetProfileDocumentDownloadUrlMutation,
  useDeleteProfileDocumentMutation,

  useUpdateAvailabilityMutation,
  useGetProfileAvailabilityQuery,
  useUpdateAvailabilityAccessMutation,

  useGetIncomingOrgCandidateInvitesQuery,
  useGetOutgoingOrgCandidateInvitesQuery,

  useGetIncomingCompanyInvitesQuery,
  useGetOutgoingCompanyInvitesQuery,
  useCreateCompanyInviteMutation,
  useActionCompanyInviteMutation,
  useCancelCompanyInviteMutation,

  useInviteOrgCandidatesMutation,
  useActionOrgCandidateInviteMutation,
  useCancelOrgCandidateInviteMutation,

  useGetCandidateJobsQuery,
  useGetCandidateApplicationsQuery,

  useGetExportSettingsQuery,

  useGetProfileRefereesQuery,
  useGetProfileRefereeByIdQuery,
  useCreateProfileRefereeMutation,
  useUpdateProfileRefereeMutation,
  useDeleteProfileRefereeMutation,
  useGetRefereeRequestsQuery,
  useGetRequestSubmissionsQuery,

  useGetOrganizationCandidatesQuery,
  useGetMyCandidatesLeanQuery,
  useLazyGetMyCandidatesLeanQuery,
  useSearchMyCandidatesLeanQuery,
  useTransformSearchDSLMutation,
  useGetOrganizationCandidateQuery,
  useGetOrganizationCandidateSyncedProfileQuery,
  useLazyGetOrganizationCandidateQuery,
  useUpdateCandidateRatingMutation,

  useCreateUnlinkedCandidateMutation,
  useImportUnlinkedCandidateMutation,
  useUpdateUnlinkedCandidateMutation,
  useUnlinkCandidateMutation,
  useRemoveCandidateMutation,

  useImportCandidateProfileMutation,
  useCreateCandidateImportJobMutation,
  useGetCandidateImportJobQuery,
  useLazyGetCandidateImportJobQuery,
  useCsvImportCandidatesMutation,

  useCreateDirectInviteMutation,

  useGetPublicCandidateInviteByIdQuery,

  useGetCandidateDocumentsQuery,
  useCreateCandidateDocumentMutation,
  useRemoveCandidateDocumentMutation,
  useUpdateCandidateDocumentMutation,
  useGetCandidateDocumentDownloadUrlMutation,

  useGetLinkedContactsQuery,

  useGetOrgCandidateActivitiesQuery,

} = candidatesApi;
